import { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import { DUAL_PANEL_SELECTABLE_TYPES, PER_PAGE } from '../../../constants/dual-panel-selector-constants'
import DualPanelSelector from '../../common/dual-panel-selector/DualPanelSelector'
import Modal from '../../common/Modal'
import SubmitButton from '../../common/buttons/SubmitButton'
import { selectProgramsData, selectSeriesData, setSearchValue } from '../../../reducers/dualPanelSelectorReducer'
import { getProgramsDualPanelSelectorThunk } from '../../../actions/programActions'
import { getAllSeries } from '../../../actions/seriesActions'
import { categoryCompare } from '../../../utility/programHelper'

import classes from './ProgramsEventsModal.module.scss'

const ProgramsEventsModal = ({
  isOpen,
  onClose,
  initialPrograms,
  initialEvents,
  onConfirm,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const programsData = useSelector(selectProgramsData)
  const seriesData = useSelector(selectSeriesData)

  const [selectedPrograms, setSelectedPrograms] = useState(initialPrograms)
  const [selectedEvents, setSelectedEvents] = useState(initialEvents)

  const fetchPrograms = useCallback((page, searchTerm, completed) =>
    dispatch(getProgramsDualPanelSelectorThunk({ page, perPage: PER_PAGE, searchTerm, completed }))
      .unwrap()
      .catch(() => toast.error(t('common.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
  , [dispatch, t])

  const fetchEvents = useCallback((page, searchTerm, completed) =>
    dispatch(getAllSeries({ filters: { search: searchTerm, completed }, page, perPage: PER_PAGE }))
      .unwrap()
      .catch(() => toast.error(t('common.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
  , [dispatch, t])

  const selectedItems = [
    ...selectedPrograms.map(p => ({ ...p, type: DUAL_PANEL_SELECTABLE_TYPES.programs })),
    ...selectedEvents.map(e => ({ ...e, type: DUAL_PANEL_SELECTABLE_TYPES.series })),
  ]

  const handleRemove = (item) => {
    if (item.type === DUAL_PANEL_SELECTABLE_TYPES.programs) {
      setSelectedPrograms(prev => prev.filter(p => p.id !== item.id))
    } else {
      setSelectedEvents(prev => prev.filter(e => e.id !== item.id))
    }
  }

  const handleClose = () => {
    onConfirm(selectedPrograms, selectedEvents)
    onClose()
  }

  const leftPanelProps = {
    tabTypes: [DUAL_PANEL_SELECTABLE_TYPES.programs, DUAL_PANEL_SELECTABLE_TYPES.series],
    tabProps: {
      [DUAL_PANEL_SELECTABLE_TYPES.programs]: {
        titleText: t('members.programsEventsModal.selectPrograms'),
        allItems: programsData.items,
        withCompletedSwitcher: true,
        searchValue: programsData.searchValue,
        onSearchValueChange: (searchValue) => dispatch(setSearchValue({ searchValue, key: DUAL_PANEL_SELECTABLE_TYPES.programs })),
        getSelectedItem: (id) => selectedPrograms.find(p => p.id === id),
        fetchItems: fetchPrograms,
        pagination: programsData.pagination,
        onCheckChange: (checked, program) => {
          if (checked) {
            setSelectedPrograms(prev => prev.filter(p => p.id !== program.id))
          } else {
            setSelectedPrograms(prev => [...prev, program])
          }
        },
        itemCategoryTitle: (program) => program.category ? program.category.name : t('members.programsEventsModal.otherPrograms'),
        itemCategoryCompare: categoryCompare,
        isLoading: programsData.isLoading,
      },
      [DUAL_PANEL_SELECTABLE_TYPES.series]: {
        titleText: t('members.programsEventsModal.selectEvents'),
        allItems: seriesData.items,
        withCompletedSwitcher: true,
        searchValue: seriesData.searchValue,
        onSearchValueChange: (searchValue) => dispatch(setSearchValue({ searchValue, key: DUAL_PANEL_SELECTABLE_TYPES.series })),
        getSelectedItem: (id) => selectedEvents.find(e => e.id === id),
        fetchItems: fetchEvents,
        pagination: seriesData.pagination,
        onCheckChange: (checked, event) => {
          if (checked) {
            setSelectedEvents(prev => prev.filter(e => e.id !== event.id))
          } else {
            setSelectedEvents(prev => [...prev, event])
          }
        },
        isLoading: seriesData.isLoading,
      },
    },
  }

  const rightPanelProps = {
    selectedItems,
    selectedItemType: DUAL_PANEL_SELECTABLE_TYPES.programs,
    summaryPlaceholder: <p className={classes.placeholder}>{t('members.programsEventsModal.emptyState')}</p>,
    onRemove: handleRemove,
  }

  return (
    <Modal className={classes.modal} title={t('members.programsEventsModal.title')} isOpen={isOpen} onClose={handleClose}>
      <div className={classes.modalContent}>
        <DualPanelSelector
          type='ProgramRoster'
          className={classes.dualPanelSelector}
          {...leftPanelProps}
          {...rightPanelProps}
        />
        <div className='mt-3 d-flex justify-content-end'>
          <SubmitButton className={classes.button} buttonMessage={t('members.programsEventsModal.addToFilters')} onClick={handleClose} />
        </div>
      </div>
    </Modal>
  )
}

ProgramsEventsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialPrograms: PropTypes.array.isRequired,
  initialEvents: PropTypes.array.isRequired,
  onConfirm: PropTypes.func.isRequired,
}

export default ProgramsEventsModal
